import { Combobox, Input, InputBase, TextInput, type useCombobox } from '@mantine/core';
import { IMaskInput } from 'react-imask';
import { TIMEZONES } from '@/config/countriesData';
import { PHONE_NUMBER_MASK } from '@/constants';
import { formatPhoneNumber } from '@/helpers';
import type { ClusterOrganizationEntry, OrganizationFormData } from './useOrganizationForm';
import styles from './EditOrganizationModal.module.sass';

const LAYOUT = {
  DROPDOWN_HEIGHT: 200,
};

const QB_ACCOUNT_MAX_LENGTH = 20;

type OrganizationContactFieldsProps = {
  formData: OrganizationFormData;
  timezoneSearch: string;
  setTimezoneSearch: (value: string) => void;
  timezoneCombobox: ReturnType<typeof useCombobox>;
  onInputChange: (field: keyof OrganizationFormData, value: string | boolean | ClusterOrganizationEntry[]) => void;
  onPhoneChange: (value: string) => void;
};

export const OrganizationContactFields = ({
  formData,
  timezoneSearch,
  setTimezoneSearch,
  timezoneCombobox,
  onInputChange,
  onPhoneChange,
}: OrganizationContactFieldsProps) => {
  return (
    <>
      <Input.Wrapper label="Phone" required>
        <Input
          component={IMaskInput}
          mask={PHONE_NUMBER_MASK}
          placeholder={PHONE_NUMBER_MASK}
          value={formatPhoneNumber(formData.phone)}
          unmask={false}
          onAccept={(value: string) => onPhoneChange(value)}
        />
      </Input.Wrapper>

      <TextInput
        label="Website"
        placeholder="Website for the Organization"
        value={formData.website}
        onChange={(e) => onInputChange('website', e.target.value)}
      />

      <Combobox
        store={timezoneCombobox}
        withinPortal={false}
        onOptionSubmit={(val) => {
          onInputChange('timezone', val);
          const selectedTimezone = TIMEZONES.find((item) => item.value === val);
          setTimezoneSearch(selectedTimezone?.label || val);
          timezoneCombobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <InputBase
            label="Timezone"
            placeholder="Type to search timezones"
            rightSection={<Combobox.Chevron />}
            value={timezoneSearch}
            onChange={(event) => {
              timezoneCombobox.openDropdown();
              timezoneCombobox.updateSelectedOptionIndex();
              setTimezoneSearch(event.currentTarget.value);
            }}
            onClick={() => timezoneCombobox.openDropdown()}
            onFocus={() => timezoneCombobox.openDropdown()}
            onBlur={() => {
              timezoneCombobox.closeDropdown();
              const selectedTimezone = TIMEZONES.find((item) => item.value === formData.timezone);
              setTimezoneSearch(selectedTimezone?.label || formData.timezone || '');
            }}
            rightSectionPointerEvents="none"
            required
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options mah={LAYOUT.DROPDOWN_HEIGHT} className={styles.dropdownOptions}>
            {TIMEZONES.filter((item) => item.label.toLowerCase().includes(timezoneSearch.toLowerCase().trim())).map(
              (item) => (
                <Combobox.Option value={item.value} key={item.value}>
                  {item.label}
                </Combobox.Option>
              )
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>

      <TextInput
        label="Quickbook #"
        placeholder="Enter quickbook #"
        value={formData.qbAccount}
        onChange={(e) => onInputChange('qbAccount', e.currentTarget.value)}
        maxLength={QB_ACCOUNT_MAX_LENGTH}
      />
    </>
  );
};
